"use client";

import * as React from "react";

import { Label } from "@unreserved/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@unreserved/ui/components/select";

import { cn } from "@/lib/utils";

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps {
  label?: string;
  placeholder?: string;
  value?: string;
  options: DropdownOption[];
  onValueChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

export function Dropdown({
  label,
  placeholder = "Select",
  value,
  options,
  onValueChange,
  error,
  disabled = false,
  className,
  id,
  name,
}: DropdownProps) {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  return (
    <div className="flex w-full flex-col items-start gap-1.5 rounded-lg">
      {label && (
        <Label
          htmlFor={id}
          className={cn(
            "text-typography-900 text-sm leading-normal font-medium",
            error && "text-error-700",
          )}>
          {label}
        </Label>
      )}
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger
          ref={triggerRef}
          id={id}
          name={name}
          className={cn(
            "border-border-400 text-typography-900 placeholder:text-typography-600 data-[placeholder]:text-typography-600 hover:bg-background-50 focus:border-primary-500 focus:ring-primary-500 flex h-12! w-full items-center justify-between gap-2 rounded-md border bg-white! px-3 text-sm shadow-none transition-colors focus:ring-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:h-11 dark:bg-white!",
            error && "border-error-700 focus:border-error-700 focus:ring-error-200",
            className,
          )}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}>
          <SelectValue
            placeholder={placeholder}
            className="data-[placeholder]:text-typography-600"
          />
        </SelectTrigger>

        <SelectContent
          className="text-typography-900 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 border-border-400 relative z-50 max-h-[300px] min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border bg-white shadow-lg"
          position="popper"
          sideOffset={4}>
          {options.map((option, index) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className={cn(
                "data-highlighted:bg-info-850 relative flex w-full cursor-pointer items-center rounded-sm px-3 py-2 text-sm transition-colors outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:text-white",
                hoveredIndex === index && "bg-info-850 text-white",
              )}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {error && (
        <div className="mt-1 flex flex-row items-center gap-1">
          <p id={`${id}-error`} className="text-error-600 text-sm" role="alert">
            {error}
          </p>
        </div>
      )}
    </div>
  );
}
